import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Header from "../../components/Header";
import { useDarkMode } from "../../App";
import { useCurrentUser, resolveListingTitle, resolveFirstListingImage } from "../../utils";
import LocationInput, { LocationData } from "../../components/LocationInput";
import carByIdQuery from "../../graphql/query/getCarsForSaleById.query";
import updateCarForSaleMutation from "../../graphql/mutations/updateCarForSale.mutation";
import Loading from "../../components/Loading";
import { useAutoResizeTextarea } from "../../hooks/useAutoResizeTextarea";
import {
  cloneEditFormFields,
  isEditFormDirty,
  type EditFormFields,
} from "./editFormDirty";
import VinColorField from "../../components/VinColorField";
import {
  EXTERIOR_COLORS,
  INTERIOR_COLORS,
  isOemSpecificColor,
} from "../../constants/vehicleColors";
import { MIN_LISTING_PHOTOS } from "../../constants/listingMedia";
import ListingMediaEditor, {
  mediaFromListing,
  isListingMediaDirty,
  type ListingMediaEditorValue,
} from "../../components/ListingMediaEditor";
import { compressImages } from "../../utils/imageCompression";
import { uploadFiles, deleteUploads } from "../../services/uploads";
import {
  stringifyListingVideoEntries,
  type ListingVideoEntry,
} from "../../utils/listingMedia";
import "./EditListing.css";

const TITLE_STATUSES = ["Clean", "Salvage", "Rebuilt", "Lemon", "Flood"];

const MIN_DESCRIPTION_LENGTH = 50;
const DESCRIPTION_TEXTAREA_MAX_HEIGHT = 320;

type EditFormData = EditFormFields;

const emptyMedia = (): ListingMediaEditorValue => ({
  photos: [],
  videos: [],
  removedKeys: [],
});

const EditListing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { darkMode } = useDarkMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hydratedListingIdRef = useRef<number | null>(null);

  const { data, loading, error } = useQuery(carByIdQuery, {
    variables: { carid: parseInt(id || "0") },
    skip: !id,
  });

  const [updateCarForSale] = useMutation(updateCarForSaleMutation);

  const [formData, setFormData] = useState<EditFormData>({
    mileage: "",
    price: "",
    description: "",
    exteriorColor: "",
    interiorColor: "",
    titleStatus: "Clean",
    location: null,
  });

  const [originalData, setOriginalData] = useState<EditFormData | null>(null);
  const [media, setMedia] = useState<ListingMediaEditorValue>(emptyMedia);
  const [originalMedia, setOriginalMedia] =
    useState<ListingMediaEditorValue | null>(null);
  const descriptionRef = useAutoResizeTextarea(
    formData.description,
    DESCRIPTION_TEXTAREA_MAX_HEIGHT
  );

  useEffect(() => {
    hydratedListingIdRef.current = null;
    setOriginalData(null);
    setOriginalMedia(null);
    setMedia(emptyMedia());
    setFormData({
      mileage: "",
      price: "",
      description: "",
      exteriorColor: "",
      interiorColor: "",
      titleStatus: "Clean",
      location: null,
    });
  }, [id]);

  useEffect(() => {
    const car = data?.carsForSaleById;
    if (!car) return;
    if (String(car.id) !== String(id)) return;
    if (hydratedListingIdRef.current === car.id) return;

    const initialData: EditFormData = {
      mileage: car.mileage?.toString() || "",
      price: car.price?.toString() || "",
      description: car.description || "",
      exteriorColor: car.exteriorColor || "",
      interiorColor: car.interiorColor || "",
      titleStatus: car.titleStatus || "Clean",
      location:
        car.latitude != null &&
        car.longitude != null &&
        `${car.latitude}` !== "" &&
        `${car.longitude}` !== ""
          ? {
              latitude: parseFloat(String(car.latitude)),
              longitude: parseFloat(String(car.longitude)),
              address: car.locationAddress || "",
            }
          : null,
    };
    const initialMedia = mediaFromListing(car.imageLoc, car.videoLoc);
    hydratedListingIdRef.current = car.id;
    setFormData(cloneEditFormFields(initialData));
    setOriginalData(cloneEditFormFields(initialData));
    setMedia(initialMedia);
    setOriginalMedia({
      photos: initialMedia.photos.map((p) =>
        p.kind === "existing" ? { ...p } : p
      ),
      videos: initialMedia.videos.map((v) =>
        v.kind === "existing" ? { kind: "existing", entry: { ...v.entry } } : v
      ),
      removedKeys: [],
    });
  }, [data, id]);

  const car = data?.carsForSaleById;
  const summaryImageUrl = useMemo(() => {
    const first = media.photos[0];
    if (first?.kind === "existing") {
      return resolveFirstListingImage([first.key], { darkMode });
    }
    if (first?.kind === "new") {
      return URL.createObjectURL(first.file);
    }
    return resolveFirstListingImage(car?.imageLoc, { darkMode });
  }, [media.photos, car?.imageLoc, darkMode]);

  useEffect(() => {
    const first = media.photos[0];
    if (!first || first.kind !== "new") return;
    return () => {
      if (summaryImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(summaryImageUrl);
      }
    };
  }, [media.photos, summaryImageUrl]);

  if (!user?.id) {
    return <Navigate to="/login" />;
  }

  if (loading && !data?.carsForSaleById) {
    return <Loading />;
  }

  if (error || !data?.carsForSaleById || !car) {
    return (
      <>
        <Header />
        <div className={`edit-listing-container ${darkMode ? "dark" : ""}`}>
          <div className="edit-listing-error">
            <h2>Listing Not Found</h2>
            <p>The listing you're trying to edit doesn't exist or has been removed.</p>
            <button
              className="wizard-btn wizard-btn-primary"
              onClick={() => navigate("/mylistings")}
            >
              Back to My Listings
            </button>
          </div>
        </div>
      </>
    );
  }

  if (car.sellerId !== user.id) {
    return (
      <>
        <Header />
        <div className={`edit-listing-container ${darkMode ? "dark" : ""}`}>
          <div className="edit-listing-error">
            <h2>Access Denied</h2>
            <p>You can only edit your own listings.</p>
            <button
              className="wizard-btn wizard-btn-primary"
              onClick={() => navigate("/mylistings")}
            >
              Back to My Listings
            </button>
          </div>
        </div>
      </>
    );
  }

  const hasChanges =
    isEditFormDirty(formData, originalData) ||
    isListingMediaDirty(media, originalMedia);

  const updateFormData = (updates: Partial<EditFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const validateForm = (): boolean => {
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price < 100) {
      alert("Please enter a valid price (minimum $100)");
      return false;
    }
    if (!formData.mileage || parseInt(formData.mileage) < 0) {
      alert("Please enter a valid mileage");
      return false;
    }
    if (formData.description.length < MIN_DESCRIPTION_LENGTH) {
      alert(
        `Please write at least ${MIN_DESCRIPTION_LENGTH} characters in your description`
      );
      return false;
    }
    if (!formData.exteriorColor) {
      alert("Please select an exterior color");
      return false;
    }
    if (!formData.interiorColor) {
      alert("Please select an interior color");
      return false;
    }
    if (!formData.location) {
      alert("Please select a location for your listing");
      return false;
    }
    if (media.photos.length < MIN_LISTING_PHOTOS) {
      alert(
        `Please keep at least ${MIN_LISTING_PHOTOS} photos on your listing`
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const newPhotoFiles = media.photos
        .filter((p): p is { kind: "new"; file: File; localId: string } =>
          p.kind === "new"
        )
        .map((p) => p.file);
      const compressed =
        newPhotoFiles.length > 0 ? await compressImages(newPhotoFiles) : [];
      const uploadedPhotoKeys =
        compressed.length > 0 ? await uploadFiles(compressed, "vehicle") : [];

      let photoUploadIdx = 0;
      const finalImageKeys: string[] = [];
      for (const item of media.photos) {
        if (item.kind === "existing") {
          finalImageKeys.push(item.key);
        } else {
          const key = uploadedPhotoKeys[photoUploadIdx++];
          if (key) finalImageKeys.push(key);
        }
      }

      const newVideos = media.videos.filter(
        (v): v is { kind: "new"; pending: any; localId: string } =>
          v.kind === "new"
      );
      const uploadedVideoKeys =
        newVideos.length > 0
          ? await uploadFiles(
              newVideos.map((v) => v.pending.file),
              "video"
            )
          : [];

      let videoUploadIdx = 0;
      const finalVideoEntries: ListingVideoEntry[] = [];
      for (const item of media.videos) {
        if (item.kind === "existing") {
          finalVideoEntries.push(item.entry);
        } else {
          const key = uploadedVideoKeys[videoUploadIdx++];
          if (!key) continue;
          finalVideoEntries.push({
            key,
            contentType: item.pending.contentType,
            durationSeconds: item.pending.durationSeconds,
            bytes: item.pending.file.size,
          });
        }
      }

      await updateCarForSale({
        variables: {
          id: parseInt(id || "0"),
          mileage: parseInt(formData.mileage),
          price: formData.price,
          description: formData.description,
          exteriorColor: formData.exteriorColor,
          interiorColor: formData.interiorColor,
          titleStatus: formData.titleStatus,
          latitude: formData.location?.latitude.toString(),
          longitude: formData.location?.longitude.toString(),
          locationAddress: formData.location?.address,
          imageLoc: JSON.stringify(finalImageKeys),
          videoLoc: stringifyListingVideoEntries(finalVideoEntries),
        },
      });

      // Best-effort S3 cleanup for removed media (do not fail the save).
      if (media.removedKeys.length > 0) {
        await deleteUploads(media.removedKeys);
      }

      alert("Listing updated successfully!");
      navigate("/mylistings");
    } catch (err) {
      console.error("Error updating listing:", err);
      alert("Failed to update listing. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate("/mylistings");
      }
    } else {
      navigate("/mylistings");
    }
  };

  const descriptionLength = formData.description.length;
  const meetsMinDescription = descriptionLength >= MIN_DESCRIPTION_LENGTH;

  return (
    <>
      <Header />
      <div className={`edit-listing-container ${darkMode ? "dark" : ""}`}>
        <div className="edit-listing-header">
          <h1>Edit Listing</h1>
          <p className="edit-listing-subtitle">
            Update the details of your listing below
          </p>
        </div>

        <div className="edit-listing-content">
          <div className="edit-listing-summary">
            <div className="summary-image">
              <img
                src={summaryImageUrl}
                alt={resolveListingTitle(car)}
              />
            </div>
            <div className="summary-details">
              <h2 className="summary-title">
                {resolveListingTitle(car)}
              </h2>
              {car.vin && <div className="summary-vin">VIN: {car.vin}</div>}
              <div className="summary-specs">
                {car.bodyStyle && <span>{car.bodyStyle}</span>}
                {car.transmission && <span>{car.transmission}</span>}
                {car.fuelType && <span>{car.fuelType}</span>}
                {car.driveType && <span>{car.driveType}</span>}
                {car.engine && <span>{car.engine}</span>}
              </div>
            </div>
          </div>

          <div className="edit-listing-form">
            <div className="form-section">
              <h3 className="form-section-title">Photos & Videos</h3>
              <ListingMediaEditor
                value={media}
                onChange={setMedia}
                darkMode={darkMode}
              />
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Pricing</h3>
              <div className="wizard-form-group">
                <label htmlFor="price">Asking Price *</label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: darkMode ? "#888" : "#666",
                      fontSize: "16px",
                    }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    id="price"
                    className="wizard-input"
                    value={formData.price}
                    onChange={(e) => updateFormData({ price: e.target.value })}
                    placeholder="25000"
                    min="100"
                    step="100"
                    style={{ paddingLeft: "32px" }}
                    required
                  />
                </div>
                <p className="field-hint">Minimum $100</p>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Vehicle Details</h3>
              <div className="details-grid">
                <div className="wizard-form-group">
                  <label htmlFor="mileage">Mileage *</label>
                  <input
                    type="number"
                    id="mileage"
                    className="wizard-input"
                    value={formData.mileage}
                    onChange={(e) => updateFormData({ mileage: e.target.value })}
                    placeholder="e.g. 45000"
                    min="0"
                    required
                  />
                </div>

                <div className="wizard-form-group">
                  <label htmlFor="titleStatus">Title Status *</label>
                  <select
                    id="titleStatus"
                    className="wizard-select"
                    value={formData.titleStatus}
                    onChange={(e) =>
                      updateFormData({ titleStatus: e.target.value })
                    }
                    required
                  >
                    {TITLE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <VinColorField
                  id="exteriorColor"
                  label="Exterior Color"
                  value={formData.exteriorColor}
                  suggestedColor={
                    isOemSpecificColor(EXTERIOR_COLORS, originalData?.exteriorColor)
                      ? originalData?.exteriorColor
                      : null
                  }
                  suggestionBadge="Manufacturer color"
                  options={EXTERIOR_COLORS}
                  onChange={(exteriorColor) => updateFormData({ exteriorColor })}
                  darkMode={darkMode}
                />

                <VinColorField
                  id="interiorColor"
                  label="Interior Color"
                  value={formData.interiorColor}
                  suggestedColor={
                    isOemSpecificColor(INTERIOR_COLORS, originalData?.interiorColor)
                      ? originalData?.interiorColor
                      : null
                  }
                  suggestionBadge="Manufacturer color"
                  options={INTERIOR_COLORS}
                  onChange={(interiorColor) => updateFormData({ interiorColor })}
                  darkMode={darkMode}
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Description</h3>
              <div className="wizard-form-group">
                <label htmlFor="description">
                  Description *
                  {!meetsMinDescription && (
                    <span
                      style={{
                        fontWeight: "normal",
                        color: darkMode ? "#888" : "#666",
                      }}
                    >
                      {" "}
                      ({descriptionLength}/{MIN_DESCRIPTION_LENGTH} min
                      characters)
                    </span>
                  )}
                </label>
                <textarea
                  ref={descriptionRef}
                  id="description"
                  className="wizard-textarea wizard-textarea-autoresize"
                  value={formData.description}
                  onChange={(e) =>
                    updateFormData({ description: e.target.value })
                  }
                  placeholder="Describe your vehicle's condition, history, features, and any recent maintenance or upgrades."
                  rows={5}
                  required
                />
                <p className="field-hint">
                  Tip: Mention service history, new parts, reason for selling
                </p>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Location</h3>
              <LocationInput
                onLocationSelect={(location: LocationData) =>
                  updateFormData({ location })
                }
                initialLocation={formData.location || undefined}
                darkMode={darkMode}
              />
            </div>
          </div>

          <div className="edit-listing-actions">
            <button
              className="wizard-btn wizard-btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="wizard-btn wizard-btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanges}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" /> Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditListing;
